import React, { useEffect, useMemo, useState } from "react";
import { jsPDF } from "jspdf";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { autoTable } from "jspdf-autotable";
import axiosInstance from "../services/baseurl";
import { getAllUsersEndPoint } from "../services/endpoints";
import {
  ListStats,
  ListPagination,
  SortHeading,
  SearchField,
  EmptyList,
} from "../components/listUi";
import UserActions from "../components/UserActions";
import shared from "../css/createBatchDesign.module.css";
import styles from "../css/listDesign.module.css";

export default function UserList() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [rowsToShow, setRowsToShow] = useState("25");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({
    key: "userId",
    direction: "descending",
  });
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    axiosInstance
      .get(getAllUsersEndPoint)
      .then((response) => {
        if (
          response.data.status !== "Success" ||
          !Array.isArray(response.data.payload)
        )
          throw new Error(response.data.message || "Unable to load users.");
        if (!cancelled) setData(response.data.payload.filter(Boolean));
      })
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.message || err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [retry]);
  const filteredData = useMemo(
    () =>
      data
        .filter((row) =>
          [row.userId, row.name, row.phone, row.address].some((value) =>
            String(value ?? "")
              .toLowerCase()
              .includes(searchQuery.trim().toLowerCase()),
          ),
        )
        .sort((a, b) => {
          const comparison = String(a[sortConfig.key] ?? "").localeCompare(
            String(b[sortConfig.key] ?? ""),
            undefined,
            { numeric: true, sensitivity: "base" },
          );
          return sortConfig.direction === "ascending"
            ? comparison
            : -comparison;
        }),
    [data, searchQuery, sortConfig],
  );
  const pageSize =
    rowsToShow === "all"
      ? Math.max(filteredData.length, 1)
      : Number(rowsToShow);
  const page = Math.min(
    currentPage,
    Math.max(1, Math.ceil(filteredData.length / pageSize)),
  );
  const paginatedData = filteredData.slice(
    (page - 1) * pageSize,
    page * pageSize,
  );
  const handleSort = (key) => {
    setSortConfig((previous) => ({
      key,
      direction:
        previous.key === key && previous.direction === "ascending"
          ? "descending"
          : "ascending",
    }));
    setCurrentPage(1);
  };
  const downloadPDF = () => {
    const doc = new jsPDF();
    autoTable(doc, {
      head: [["S.No", "User Id", "Name", "Mobile No", "Address"]],
      body: filteredData.map((row, index) => [
        index + 1,
        row.userId,
        row.name,
        row.phone,
        row.address,
      ]),
    });
    doc.save("users.pdf");
  };

  const downloadExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const data = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(data, "users.xlsx");
  };

  return (
    <div className="dashboard-glass admin-theme">
      <main className="admin-page">
        <header className={shared.pageHeader}>
          <div>
            <span className={shared.eyebrow}>YOUR COMMUNITY</span>
            <h1>User directory</h1>
            <p>A clear view of the people behind your collections.</p>
          </div>
          <span className={shared.draftBadge}>
            <span />
            Customer directory
          </span>
        </header>
        {/* <ListStats
          items={[
            {
              label: "Total users",
              value: loading || error ? "--" : data.length,
              note: "Customers in your directory",
            },
            {
              label: "With mobile numbers",
              value:
                loading || error
                  ? "--"
                  : data.filter((row) => row.phone).length,
              note: "Contact information available",
            },
            {
              label: "Matching users",
              value: loading || error ? "--" : filteredData.length,
              note: "Based on your current search",
            },
          ]}
        /> */}
        <section
          className={styles.tablePanel}
          aria-label="User directory table"
        >
          <header className={styles.panelHeading}>
            <div>
              <h2>Your users</h2>
              <p>Search by name, mobile number, address, or user ID.</p>
            </div>
            <div className={styles.exports}>
              <button
                type="button"
                onClick={downloadPDF}
                disabled={loading || Boolean(error) || !filteredData.length}
              >
                Export PDF
              </button>
              <button
                type="button"
                onClick={downloadExcel}
                disabled={loading || Boolean(error) || !filteredData.length}
              >
                Export Excel
              </button>
            </div>
          </header>
          <div className={styles.toolbar}>
            <SearchField
              label="Search users"
              placeholder="Find a name, number, or user ID"
              value={searchQuery}
              onChange={(event) => {
                setSearchQuery(event.target.value);
                setCurrentPage(1);
              }}
            />
            <label className={styles.pageSize}>
              Rows
              <select
                value={rowsToShow}
                onChange={(event) => {
                  setRowsToShow(event.target.value);
                  setCurrentPage(1);
                }}
              >
                {["25", "50", "100", "all"].map((value) => (
                  <option key={value} value={value}>
                    {value === "all" ? "All" : value}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {loading ? (
            <div className={styles.loading} role="status">
              Loading your users...
            </div>
          ) : error ? (
            <div className={styles.empty} role="alert">
              <h3>Users could not be loaded</h3>
              <p>{error}</p>
              <button
                type="button"
                onClick={() => setRetry((value) => value + 1)}
              >
                Try again
              </button>
            </div>
          ) : !paginatedData.length ? (
            <EmptyList
              title={
                searchQuery ? "No matching users" : "Your community starts here"
              }
              description={
                searchQuery
                  ? "Try a different name, number, or user ID."
                  : "Registered users will appear in this directory."
              }
            />
          ) : (
            <div
              className={styles.tableShell}
              role="region"
              aria-label="User records"
              tabIndex={0}
            >
              <table>
                <thead>
                  <tr>
                    <SortHeading
                      label="Customer"
                      sortKey="name"
                      config={sortConfig}
                      onSort={handleSort}
                    />
                    <SortHeading
                      label="User ID"
                      sortKey="userId"
                      config={sortConfig}
                      onSort={handleSort}
                    />
                    <SortHeading
                      label="Mobile number"
                      sortKey="phone"
                      config={sortConfig}
                      onSort={handleSort}
                    />
                    <SortHeading
                      label="Address"
                      sortKey="address"
                      config={sortConfig}
                      onSort={handleSort}
                    />
                  <th scope="col">Actions</th>
</tr>
                </thead>
                <tbody>
                  {paginatedData.map((row, index) => (
                    <tr key={row.userId ?? index}>
                      <td>
                        <div className={styles.customer}>
                          <span className={styles.avatar}>
                            {(row.name || "U").charAt(0).toUpperCase()}
                          </span>
                          <div>
                            <strong>{row.name || "Unnamed user"}</strong>
                            <small>Customer account</small>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={styles.idBadge}>#{row.userId}</span>
                      </td>
                      <td className={styles.nowrap}>
                        {row.phone || (
                          <span className={styles.muted}>Not provided</span>
                        )}
                      </td>
                      <td className={styles.address}>
                        {row.address || (
                          <span className={styles.muted}>Not provided</span>
                        )}
                      </td>
                    <td><UserActions user={row} onUpdated={updated => {
                        setData(previous => previous.map(user => user.userId === updated.userId ? updated : user));
                        setRetry(previous => previous + 1);
                      }} onDeleted={id => {
                        setData(previous => previous.filter(user => user.userId !== id));
                        setRetry(previous => previous + 1);
                      }} /></td>
</tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <ListPagination
            total={filteredData.length}
            page={page}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
          />
        </section>
        <p className={styles.footnote}>
          Edit user details or delete an account using the actions in each row.
          Deletion requires confirmation.
        </p>
      </main>
    </div>
  );
}
